import { AnimatePresence } from 'framer-motion';
import moment from 'moment';
import { useState } from 'react';
import { BsFillClockFill } from 'react-icons/bs';
import { FaUser } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../../contexts/AuthProvider';
import { useBookingContext } from '../../../contexts/BookingProvider';
import { AvailableAssociate } from '../../../types/appointments';
import { Client } from '../../../types/users';
import { showFullName, showPicture } from '../../../utils/commonFunctions';
import AcademiaRating from '../AcademiaRating';
import CardBadge from '../Badges/CardBadge';
import AcademiaButton from '../Buttons/AcademiaButton';
import AvailableSlotsModal from '../Modals/AvailableSlotsModal';
import AssociateCardCount from './AssociateCardCount';

const AssociateCard = ({ associate }: { associate: AvailableAssociate }) => {
  // destructuring the associate object here
  const {
    first_name,
    middle_name,
    last_name,
    profile_picture,
    username,
    total_appointments,
    total_languages,
    feedback_count,
    associate_rating,
    credits_per_appointment,
    slot_duration,
    language_names,
  } = associate || {};

  // integration of react hooks here
  const [showModal, setShowModal] = useState<boolean>(false);

  // integration of context hooks here
  const { selectedDate, setSearchDate, setShowUpdateClientModal } =
    useBookingContext();
  const { currentUser, currentUserType } = useAuthContext();

  // rendering associate card component here
  return (
    <div className='bg-base-white p-4 xl:p-2 2xl:p-4 rounded-lg shadow-academia-sm'>
      <figure className='flex items-center justify-center'>
        {profile_picture ? (
          <div className='size-24 rounded-full overflow-hidden'>
            <img
              className='h-full w-full object-cover'
              src={showPicture(profile_picture)}
              alt={`avatar of ${username}`}
            />
          </div>
        ) : (
          <div className='flex items-center justify-center bg-base-light-gray size-24 rounded-full'>
            <FaUser className='size-14 text-gray-300' />
          </div>
        )}
      </figure>

      <figcaption className='mt-3 space-y-2'>
        <h3 className='text-center text-xl text-legal-dark-blue font-semibold'>
          {showFullName(first_name, middle_name, last_name)}
        </h3>

        <p className='text-center text-xl font-medium text-base-dark-green'>
          {credits_per_appointment} credits
          <span className='text-sm font-normal text-base-orange'>/slot</span>
        </p>

        <div className='flex items-center justify-center !mt-1'>
          <div className='flex items-center gap-1 text-base-dark-green font-semibold text-sm border-2 border-base-dark-green rounded-full w-fit px-3 bg-base-light-green/30'>
            <BsFillClockFill />
            <p>Slot: {slot_duration} Minutes</p>
          </div>
        </div>

        <div className='flex items-center justify-center'>
          <AcademiaRating size={30} ratingValue={associate_rating} isReadonly />
        </div>

        <div className='grid grid-cols-3 !mt-7'>
          <AssociateCardCount count={total_appointments} text='Appointments' />
          <AssociateCardCount count={feedback_count} text='Reviews' />
          <AssociateCardCount count={total_languages} text='Languages' />
        </div>

        <div className='flex flex-wrap items-center gap-2 !mt-4'>
          {language_names.slice(0, 5).map((language, index) => (
            <CardBadge key={index} badgeText={language} />
          ))}
          {language_names.length > 5 ? (
            <span className='text-legal-dark-green font-semibold italic'>
              ... +{language_names.length - 5} More
            </span>
          ) : null}
        </div>

        <div className='!mt-3'>
          <AcademiaButton
            type='button'
            onClick={() => {
              if (
                currentUserType === 'client' &&
                !(currentUser as Client).is_profile_complete
              ) {
                toast.warn('Please, complete your profile first!');
                setShowUpdateClientModal(true);
                return;
              }

              setShowModal(true);
              setSearchDate(
                moment(selectedDate, 'DD-MM-YYYY').format('YYYY-MM-DD')
              );
            }}
            fullWidth
            additionalClassNames='!py-2'
          >
            View Available Slots
          </AcademiaButton>
        </div>
      </figcaption>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showModal && (
          <AvailableSlotsModal
            associate={associate}
            date={moment(selectedDate, 'DD-MM-YYYY').format('YYYY-MM-DD')}
            handleModalClose={() => setShowModal(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AssociateCard;
